import { Injectable, inject, WritableSignal, Signal } from '@angular/core';
import { URLS } from '@constants/urls';
import { PERMISSIONS } from '@constants/permissions';
import { CognitoService } from '@services/cognito/cognito.service';
import { PAGE } from '@constants/constants';
import { Apollo } from 'apollo-angular';
import {
  GetPermissionsQuery,
  GetPermissionsQueryVariables,
  GetUserRoleQuery,
  Roles,
} from 'gql/graphql';
import { getPermissionsQuery, getUserRoleQuery } from '@graphql/user';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private _permissions: WritableSignal<Record<
    keyof URLS,
    { viewer: boolean; editing: boolean }
  > | null> = inject(PERMISSIONS);
  permissions: Signal<
    Record<keyof URLS, { viewer: boolean; editing: boolean }> | undefined | null
  >;

  constructor(private cognitoService: CognitoService, private apollo: Apollo) {
    this.permissions = this._permissions.asReadonly();
    const userPermissions = this.cognitoService.loggedUser()?.permissions;
    this._permissions.set(userPermissions ? JSON.parse(userPermissions) : {});
  }

  getUserRole(): Promise<Roles | void> {
    return new Promise((resolve, reject) => {
      this.apollo
        .watchQuery<GetUserRoleQuery>({
          query: getUserRoleQuery,
        })
        .valueChanges.subscribe({
          next: (res) => {
            this.cognitoService.updateLoggedInUserRole(res.data.getUserRole);
            resolve(res.data.getUserRole);
          },
          error: (err) => {
            console.error(err);
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
            resolve();
          },
        });
    });
  }

  getPermissions(role: Roles): Promise<void> {
    return new Promise((resolve, reject) => {
      this.apollo
        .watchQuery<GetPermissionsQuery, GetPermissionsQueryVariables>({
          query: getPermissionsQuery,
          variables: {
            role,
          },
        })
        .valueChanges.subscribe({
          next: (res) => {
            const permissions = res.data.getPermissions.reduce(
              (accum, curr) => {
                return {
                  ...accum,
                  [curr!.key]: {
                    viewer: curr!.value.viewer,
                    editing: curr!.value.editing,
                  },
                };
              },
              {} as Record<keyof URLS, { viewer: boolean; editing: boolean }>
            );
            console.log('Permissions:', permissions);
            this.cognitoService.updateLoggedInUserPermissions(permissions);
            this.setPermissions(
              JSON.parse(this.cognitoService.loggedUser()?.permissions!)
            );
            resolve();
          },
          error: (err) => {
            console.error(err);
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
            resolve();
          },
        });
    });
  }

  setPermissions(
    permissions: Record<
      keyof URLS,
      { viewer: boolean; editing: boolean }
    > | null
  ): void {
    this._permissions.set(permissions);
  }

  canView(url: keyof URLS): boolean {
    return !!this.permissions()?.[url]?.viewer;
  }

  canEdit(url: keyof URLS): boolean {
    return !!this.permissions()?.[url]?.editing;
  }

  canEditPage(page: PAGE) {
    const url = Object.keys(URLS).find(
      (key) => URLS[key as keyof URLS] === page.link?.split('/').at(1)
    ) as keyof URLS;
    return this.canEdit(url);
  }
}
